<svelte:window
    on:mousemove={pointerMove}
    on:mouseup={dragEnd}
    on:touchend={dragEnd}
    on:touchmove={pointerMove}
/>

<CreateTopicModal
    bind:enableCreateTopicModal={showCreateTopicModal}
    {createTopicModal}
    on:createTopic={createNewTopic}
    on:cancel={cancelTopic}
/>

<div
    class={`its-plans-view-switch ${
        showEmptyState
            ? "its-plans-view-switch-position-empty"
            : "its-plans-view-switch-position-list"
    }`}
>
    {#if config.canEdit && $topicsData.topics.length !== 0}
        <div class="its-plans__toolbar its-plans__toolbar-topic-offset">
            <div class="its-plans__toolbar_items">
                <Button
                    type="primary"
                    text={config.createTopicText}
                    large
                    on:click={openCreateTopicModal}
                />
                <TopicActions on:endOfActionOnSelectedTopics={endOfActionOnSelectedTopics} />
            </div>
            {#if config.isImportEnabled}
                <UseReadyMadeContentLink
                    href={`/Courses/${
                        config.courseId
                    }/ImportContent?importTopic=true&returnUrl=${encodeURI(topicTabHref)}`}
                    linktext={i18n.useReadyMadeContent}
                    emptyState={false}
                ></UseReadyMadeContentLink>
            {/if}
        </div>
    {/if}

    <TableViewLink
        {config}
        {i18n}
        isDataLoading={$topicsData.loading}
        backExtraParams={{
            selectedTab
        }}
    />
</div>

{#if $topicsData.loading}
    <div class="its-plans__loading">
        <span class="screen-reader">{i18n.loading}</span>
    </div>
{/if}

{#if showEmptyState}
    <EmptyState
        iconName="plan-topics"
        title={titleEmptyTopics}
        description={descriptionEmptyTopics}
        {showButton}
        {buttonText}
        on:click={openCreateTopicModal}
    />
    {#if config.canEdit && config.isImportEnabled}
        <UseReadyMadeContentLink
            href={`/Courses/${config.courseId}/ImportContent?importTopic=true&returnUrl=${encodeURI(
                topicTabHref
            )}`}
            linktext={i18n.useReadyMadeContent}
            emptyState={true}
        ></UseReadyMadeContentLink>
    {/if}
{/if}

<section class="its-topics__list-section" bind:this={listSection}>
    <ul
        class="its-topics__list"
        tabindex="-1"
        aria-labelledby="tab-topics"
        bind:this={listElement}
        role={keyboardReorder ? "listbox" : undefined}
        aria-activedescendant={keyboardReorderActiveId
            ? `topic-card-${keyboardReorderActiveId}`
            : undefined}
        on:keydown={listKeyDown}
        on:blur={listBlur}
    >
        {#if !showEmptyState}
            {#each $topicsData.topics as topic (topic.id)}
                <TopicCard
                    topicId={topic.id}
                    id="topic-card-{topic.id}"
                    name={topic.name}
                    href="{topic.url}?ChildId={config.childId}"
                    color={topic.borderColor}
                    showSelect={config.canEdit}
                    showDrag={config.canEdit}
                    planCount={topic.planCount}
                    startDate={topic.start}
                    endDate={topic.stop}
                    on:dragStart={e => dragStart(e.detail, topic)}
                    on:keyboardDragStart={() => keyboardDragStart(topic)}
                    keyboardReorderSelected={keyboardReorder &&
                        keyboardReorderActiveId === topic.id}
                    keyboardReorderActive={keyboardReorder}
                    dragAndDropSelected={dragSelectedTopic && topic.id === dragSelectedTopic.id}
                />
            {/each}
        {/if}

        {#if $topicsData.plansWithoutTopicCount}
            <TopicCard
                name={config.withoutTopic}
                href={topicCardHref}
                showSelect={false}
                planCount={$topicsData.plansWithoutTopicCount}
                startDate={null}
                endDate={null}
                uncategorized
            />
        {/if}
    </ul>

    {#if dragSelectedTopic}
        <div class="its-topics__topic-dragging" style={draggedElementStyle}>
            <TopicCard
                name={dragSelectedTopic.name}
                href="./"
                color={dragSelectedTopic.borderColor}
                showSelect={true}
                showDrag={true}
                planCount={dragSelectedTopic.planCount}
                startDate={dragSelectedTopic.start}
                endDate={dragSelectedTopic.stop}
            />
        </div>
    {/if}
</section>

<script>
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import EmptyState from "@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte";

    import i18n from "@its-ui/planner-common/i18n";
    import config from "@its-ui/planner-common/config";
    import { createTopic, setTopicOrder } from "@its-ui/planner-common/api";
    import { postNewPlannerKpi } from "@its-ui/planner-common/helpers/kpiHelper";

    import CreateTopicModal from "@its-ui/planner-common/components/CreateTopicModal/CreateTopicModal.svelte";
    import TopicActions from "@its-ui/planner-common/components/TopicActions/TopicActions.svelte";
    import UseReadyMadeContentLink from "@its-ui/planner-common/components/UseReadyMadeContentLink/UseReadyMadeContentLink.svelte";
    import TableViewLink from "@its-ui/planner-common/components/TableViewLink/TableViewLink.svelte";

    import TopicCard from "../TopicCard/TopicCard.svelte";

    import { topicsData } from "@its-ui/planner-common/logic/plans/stores";
    import { normalizeKey } from "@itslearning/atlas/keyboard/normalize";
    import { Keys } from "@itslearning/atlas/keyboard/keys";
    import { debounce } from "@itslearning/atlas/dom/Ratecontrol";
    import { onMount, createEventDispatcher } from "svelte";

    let keyboardReorder = false;
    let keyboardReorderActiveId;
    let reorderDragHandleId;
    let draggedElementTop;
    let dragSelectedTopic = undefined;
    let debounceSaveTopicOrder;
    let listSection;
    let listElement;
    let dragScrolling = false;

    const topicTabHref = window.location.href.replace(/\/(all|current|past|withoutDate)/g, "/topics");

    const descriptionEmptyTopics = config.canEdit
        ? config.emptyStateTopicsTabDescriptionTeacher
        : config.emptyStateTopicsTabDescriptionStudent;
    const titleEmptyTopics = config.canEdit
        ? config.emptyStateTopicsTabTitleTeacher
        : config.emptyStateTopicsTabTitleStudent;
    const showButton = config.canEdit;
    const buttonText = config.createTopicText;

    let showCreateTopicModal = false;


    export let selectedTab;
    const createTopicModal = "create-topic-modal";
    const dispatch = createEventDispatcher();

    $: enableCreateTopicModal = showCreateTopicModal;

    $: showEmptyState = $topicsData.topics.length === 0 && !$topicsData.loading;

    $: draggedElementStyle = dragSelectedTopic ? `top: ${draggedElementTop}px;` : "";

    $: topicCardHref = config.publicAccessKey
        ? `/plans/public/${config.publicAccessKey}/topics/0`
        : `/plans/courses/${config.courseId}/topics/0?ChildId=${config.childId}`;

    onMount(() => {
        // Debounce saves to the topic order to limit requests and to avoid race conditions
        // when two changes are made in quick succession.
        const runDebounceSaveTopicOrder = debounce(saveTopicOrder, 1000);

        debounceSaveTopicOrder = () => {
            runDebounceSaveTopicOrder();
        };
    });

    // Show the create topic modal
    function openCreateTopicModal() {
        showCreateTopicModal = true;
    }

    async function createNewTopic({ detail }) {
        await createTopic(detail.title, detail.colour);

        showCreateTopicModal = false;
        await postNewPlannerKpi(config.kpi.kpiMeasurementCreateNewTopic);
        dispatch("createTopic");
    }

    function cancelTopic() {
        showCreateTopicModal = false;
    }

    function endOfActionOnSelectedTopics() {
        dispatch("endOfActionOnSelectedTopics");
    }

    function moveDown(element, index) {
        if (index >= $topicsData.topics.length - 1) {
            return;
        }

        const target = $topicsData.topics[index + 1];

        moveTopic(element, target, false);

        debounceSaveTopicOrder();
    }

    function moveUp(element, index) {
        if (index <= 0) {
            return;
        }

        const target = $topicsData.topics[index - 1];

        moveTopic(element, target, true);

        debounceSaveTopicOrder();
    }

    // Moves one topic to a target topic's position in the list, inserting
    // it either immediately before or after depending on insertBefore
    function moveTopic(topic, target, insertBefore) {
        const newElements = [...$topicsData.topics];
        const fromIndex = newElements.findIndex(e => e.id === topic.id);

        newElements.splice(fromIndex, 1);

        const toIndex = newElements.findIndex(e => e.id === target.id) + (insertBefore ? 0 : 1);

        newElements.splice(toIndex, 0, topic);

        $topicsData.topics = newElements;
    }

    function keyboardDragStart(topic) {
        reorderDragHandleId = document.activeElement.id;

        keyboardReorder = true;
        keyboardReorderActiveId = topic.id;

        setTimeout(() => listElement.focus(), 0);
    }

    function listKeyDown(event) {
        if (!keyboardReorder) {
            return;
        }

        const index = $topicsData.topics.findIndex(e => e.id === keyboardReorderActiveId);
        const activeTopic = $topicsData.topics[index];

        switch (normalizeKey(event.key)) {
            case Keys.UP:
                moveUp(activeTopic, index);
                event.preventDefault();
                scrollTopicToView(activeTopic.id);
                break;
            case Keys.DOWN:
                moveDown(activeTopic, index);
                event.preventDefault();
                scrollTopicToView(activeTopic.id);
                break;
            case Keys.ESCAPE:
            case Keys.SPACE:
            case Keys.TAB:
                event.stopPropagation();
                event.preventDefault();

                keyboardReorder = false;
                keyboardReorderActiveId = undefined;

                if (reorderDragHandleId) {
                    setTimeout(() => document.getElementById(reorderDragHandleId).focus(), 0);
                }

                break;
        }
    }

    function scrollTopicToView(topicId) {
        setTimeout(() => {
            document
                .getElementById(`topic-card-${topicId}`)
                .scrollIntoView({ behavior: "smooth", block: "center" });
        }, 0);
    }

    function listBlur() {
        keyboardReorder = false;
        keyboardReorderActiveId = undefined;
    }

    // Start dragging by setting the dragSelectedTopic and call pointerMove to position it correctly
    function dragStart(event, topic) {
        dragSelectedTopic = topic;

        pointerMove(event);
    }

    // While dragging an topic set its y position to the cursor's y pos and check for
    // other topics under the pointer to re-order the list
    function pointerMove(event) {
        if (dragSelectedTopic) {
            event.preventDefault();
            event.stopPropagation();

            const cursorPosY = event.touches ? event.touches[0].pageY : event.pageY;
            const scrollTop = document.body.parentElement.scrollTop;
            const offsetTop = scrollTop + listSection.getBoundingClientRect().y;

            draggedElementTop = cursorPosY - offsetTop;

            let elementRect;
            let elementTop;
            let dragOverElement;

            // Iterate over <li>s in the list and check if the pointer is within any of them
            for (let i = 0; i < listElement.children.length; i++) {
                elementRect = listElement.children[i].getBoundingClientRect();
                elementTop = elementRect.y + scrollTop;

                if (cursorPosY > elementTop && cursorPosY < elementTop + elementRect.height) {
                    dragOverElement = $topicsData.topics[i];

                    break;
                }
            }

            // If a topic (other than the dragged one) is hovered re-order the list to place the
            // dragged topic above or below the dragOverElement depending on pointer position.
            if (dragOverElement && dragOverElement.id !== dragSelectedTopic.id) {
                const insertBefore = cursorPosY - elementTop < elementRect.height / 2;

                moveTopic(dragSelectedTopic, dragOverElement, insertBefore);
            }

            // If dragged element is at the bottom or top of the page start the scroll function
            if (cursorPosY - scrollTop > window.innerHeight - elementRect.height) {
                if (!dragScrolling) {
                    dragScrolling = true;
                    scrollWithDrag(true, cursorPosY);
                }
            } else if (cursorPosY - scrollTop < elementRect.height) {
                if (!dragScrolling) {
                    dragScrolling = true;
                    scrollWithDrag(false, cursorPosY);
                }
            } else {
                // Stop the scroll function when the cursor is out of the scroll area
                dragScrolling = false;
            }
        }
    }

    function scrollWithDrag(down) {
        // When scrolling down, if the bottom of the list is 25px above the bottom of the page
        if (down && listSection.getBoundingClientRect().bottom + 25 <= window.innerHeight) {
            dragScrolling = false;
        }

        // When scrolling up, if the page is already at the top, stop scrolling
        if (!down && document.body.parentElement.scrollTop <= 0) {
            dragScrolling = false;
        }

        if (!dragScrolling) {
            return;
        }

        const scrollAmount = down ? 3 : -3;

        document.body.parentElement.scrollTop += scrollAmount;

        // Move the dragged element to keep up with the moving page
        draggedElementTop += scrollAmount;

        // This function keeps calling itself until the max scroll is reached or dragScrolling is
        // false. This keeps the scroll smooth instead of jumping only when the pointer moves.
        setTimeout(() => {
            scrollWithDrag(down);
        }, 5);
    }

    // The list is reordered while dragging so once it is released trigger a save and stop
    // dragging the element
    function dragEnd() {
        if (dragSelectedTopic) {
            debounceSaveTopicOrder();

            dragSelectedTopic = null;
            dragScrolling = false;
        }
    }

    // Posts the new topic order to the API
    async function saveTopicOrder() {
        await setTopicOrder($topicsData.topics.map(e => e.id));
    }
</script>
